import { useEffect, useState } from "react";
import { dateOf } from "@bd/sim";
import { Icon, Logo } from "@bd/ui-kit";
import { useGame } from "../store";
import { callServer, claimPlayer, createSeason, joinSeason, type Member, type Season } from "./api";
import { discordReady, enterSeason, sb, session, signInWithDiscord, signInWithPassword, signOut } from "./session";
import { t, tdate } from "../i18n";

type Stage = "loading" | "offline" | "signed-out" | "not-invited" | "ready";
/** The one small form that can be open: a new season, or joining one (needs your company's name). */
type Form = { kind: "new" } | { kind: "join"; season: string } | null;

/** Play with the cousins: sign in with Discord, then make, join, start or continue a season. */
export function Lobby() {
  const set = useGame((s) => s.set);
  const [stage, setStage] = useState<Stage>("loading");
  const [me, setMe] = useState<{ id: string; name: string } | null>(null);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [members, setMembers] = useState<Record<string, Member[]>>({});
  const [names, setNames] = useState<Record<string, string>>({});
  const [form, setForm] = useState<Form>(null);
  const [company, setCompany] = useState("");
  const [seasonName, setSeasonName] = useState(() => t("Cousins' season"));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [discord, setDiscord] = useState<boolean | null>(null);
  const qa = new URLSearchParams(location.search).has("qa");

  const load = async () => {
    if (!sb) return setStage("offline");
    const s = await session();
    if (!s) return setStage("signed-out");
    const name = await claimPlayer(sb).catch(() => null);
    if (!name) return setStage("not-invited");
    setMe({ id: s.user.id, name });
    const [{ data: ss }, { data: ms }, { data: ps }] = await Promise.all([
      sb.from("seasons").select("*").order("created_at", { ascending: false }),
      sb.from("season_members").select("*"),
      sb.from("players").select("user_id, display_name"),
    ]);
    setSeasons((ss ?? []) as Season[]);
    const by: Record<string, Member[]> = {};
    for (const m of (ms ?? []) as Member[]) (by[m.season_id] ??= []).push(m);
    for (const k of Object.keys(by)) by[k].sort((a, b) => a.seat - b.seat);
    setMembers(by);
    setNames(Object.fromEntries((ps ?? []).map((p: { user_id: string; display_name: string }) => [p.user_id, p.display_name])));
    setStage("ready");
  };
  useEffect(() => {
    void discordReady().then(setDiscord);
    void load();
    const sub = sb?.auth.onAuthStateChange(() => void load());
    return () => sub?.data.subscription.unsubscribe();
  }, []);

  const act = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await f();
      setForm(null);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t("That didn't work. Try again."));
    }
    setBusy(false);
  };

  const mine = seasons.filter((s) => members[s.id]?.some((m) => m.user_id === me?.id));
  // test seasons (bots, soak tests) only show up for QA
  const open = seasons.filter((s) => s.status === "lobby" && !mine.includes(s) && (qa || !s.staging));
  const companyOk = company.trim().length > 0;

  /** Everyone in a season as a row of initials; names and companies on hover. */
  const dots = (s: Season) => (
    <span className="row" style={{ gap: 4 }}>
      {(members[s.id] ?? []).map((m) => (
        <span key={m.user_id} className={`seat-dot ${m.user_id === me?.id ? "here" : ""}`} title={`${names[m.user_id] ?? "?"} · ${m.company_name}`}>
          {(names[m.user_id] ?? "?").slice(0, 1).toUpperCase()}
        </span>
      ))}
    </span>
  );
  const status = (s: Season) =>
    s.status === "lobby" ? t("{n} of 4", { n: members[s.id]?.length ?? 0 }) : s.status === "finished" ? t("Finished") : tdate(dateOf(s.week).label);
  const companyInput = (
    <input value={company} maxLength={32} placeholder={t("Your company's name")} onChange={(e) => setCompany(e.target.value)} style={{ flex: 1, minWidth: 140 }} data-testid="online-company" autoFocus />
  );

  return (
    <div className="title">
      <div className="panel title-card lobby" data-testid="lobby">
        <div className="row">
          <button className="icon-btn" onClick={() => set({ lobby: false })} aria-label={t("Back")} title={t("Back")} data-testid="lobby-back"><Icon name="back" /></button>
          <Logo size={36} />
          <h1 className="display" style={{ margin: 0, fontSize: "1.8rem" }}>{t("The cousins")}</h1>
        </div>

        {stage === "loading" && <span className="muted">{t("Connecting…")}</span>}
        {stage === "offline" && <span className="muted">{t("Online play isn't available here.")}</span>}

        {stage === "signed-out" && (
          <div className="stack">
            <button className="btn primary" disabled={discord === false} onClick={() => void signInWithDiscord()} data-testid="discord">{t("Sign in with Discord")}</button>
            {discord === false && <span className="muted" style={{ fontSize: "0.85rem" }} data-testid="discord-off">{t("Discord sign-in isn't switched on yet.")}</span>}
            {qa && <TestSignIn onDone={() => void load()} />}
          </div>
        )}

        {stage === "not-invited" && (
          <div className="stack">
            <span>{t("This Discord account isn't invited.")}</span>
            <button className="btn" onClick={() => void signOut().then(load)}>{t("Sign out")}</button>
          </div>
        )}

        {stage === "ready" && me && (
          <div className="stack">
            {mine.map((s) => {
              const creator = s.created_by === me.id;
              return (
                <div key={s.id} className="card spread" style={{ gap: 10 }} data-testid={`season-${s.id}`}>
                  <div className="stack" style={{ gap: 4, minWidth: 0 }}>
                    <strong>{s.name}</strong>
                    <span className="row" style={{ gap: 8 }}>{dots(s)}<span className="muted" style={{ fontSize: "0.85rem" }}>{status(s)}</span></span>
                  </div>
                  {s.status === "lobby" && creator && (
                    <button className="btn primary small" disabled={busy} title={t("Start when everyone has joined")} onClick={() => void act(async () => { const r = await callServer(sb!, "start", s.id); if (r.error) throw new Error(r.error); })} data-testid="start-season">{t("Start")}</button>
                  )}
                  {s.status === "lobby" && !creator && <span title={t("Not started")}><Icon name="pause" size={16} color="var(--muted)" /></span>}
                  {s.status !== "lobby" && (
                    <button className="btn primary small" disabled={busy} onClick={() => void act(() => enterSeason(s.id))} data-testid="play-season"><Icon name={s.status === "finished" ? "trophy" : "play"} size={14} />{s.status === "finished" ? t("Finale") : t("Play")}</button>
                  )}
                </div>
              );
            })}

            {open.map((s) => (
              <div key={s.id} className="card stack" style={{ gap: 8 }} data-testid={`open-${s.id}`}>
                <div className="spread" style={{ gap: 10 }}>
                  <div className="stack" style={{ gap: 4 }}>
                    <strong>{s.name}</strong>
                    <span className="row" style={{ gap: 8 }}>{dots(s)}<span className="muted" style={{ fontSize: "0.85rem" }}>{status(s)}</span></span>
                  </div>
                  {form?.kind !== "join" || form.season !== s.id ? (
                    <button className="btn small" disabled={busy || (members[s.id]?.length ?? 0) >= 4} onClick={() => setForm({ kind: "join", season: s.id })} data-testid="join-season">
                      {(members[s.id]?.length ?? 0) >= 4 ? t("Full") : t("Join")}
                    </button>
                  ) : null}
                </div>
                {form?.kind === "join" && form.season === s.id && (
                  <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                    {companyInput}
                    <button className="btn primary small" disabled={busy || !companyOk} onClick={() => void act(() => joinSeason(sb!, s.id, company))} data-testid="join-confirm">{t("Join")}</button>
                  </div>
                )}
              </div>
            ))}

            {form?.kind === "new" ? (
              <div className="card stack" style={{ gap: 6 }}>
                <input value={seasonName} maxLength={40} onChange={(e) => setSeasonName(e.target.value)} placeholder={t("Season name")} aria-label={t("Season name")} data-testid="season-name" />
                <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
                  {companyInput}
                  <button className="btn primary small" disabled={busy || !companyOk || !seasonName.trim()} onClick={() => void act(() => createSeason(sb!, seasonName, company, { staging: qa }))} data-testid="create-season">{t("Create")}</button>
                  <button className="icon-btn" style={{ width: 32, height: 32 }} onClick={() => setForm(null)} aria-label={t("Cancel")} title={t("Cancel")}><Icon name="close" size={16} /></button>
                </div>
              </div>
            ) : (
              <button className="btn" onClick={() => setForm({ kind: "new" })} data-testid="new-season"><Icon name="plus" size={16} />{t("New season")}</button>
            )}

            <div className="spread muted" style={{ fontSize: "0.85rem" }}>
              <span>{me.name}</span>
              <button className="btn small ghost" onClick={() => void signOut().then(load)}>{t("Sign out")}</button>
            </div>
          </div>
        )}
        {err && <span className="bad" role="alert">{err}</span>}
      </div>
    </div>
  );
}

/** QA and bots only (?qa in the address): the test accounts sign in with a password. */
function TestSignIn({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="card stack" style={{ gap: 6 }}>
      <span className="caps">{t("Test account")}</span>
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("Email")} data-testid="qa-email" />
      <input value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t("Password")} type="password" data-testid="qa-password" />
      <button className="btn small" onClick={() => void signInWithPassword(email, pw).then((e) => (e ? setErr(e) : onDone()))} data-testid="qa-signin">{t("Sign in")}</button>
      {err && <span className="bad">{err}</span>}
    </div>
  );
}
